import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ApiClientError, getFamBySlug } from "../../../../lib/api";
import { useCreateIdea } from "../../../../lib/api/ideas";
import type {
	CreateIdeaRequest,
	IdeaDirection,
	VoteWindowHours,
} from "../../../../lib/types";
import { getCurrentUser } from "../../../../lib/auth";
import { BacktestCard } from "../../../../components/BacktestCard";

// Vote-window options mirror the vote service's AllowedVoteWindowHours.
// 2h is the free-tier default + only allowed window for free Fams. 24/72/168
// are paid-only; the buttons render disabled with an "Upgrade" sub-label
// when the active Fam is on the free tier.
const VOTE_WINDOWS: {
	hours: VoteWindowHours;
	label: string;
	sub: string;
	paidOnly: boolean;
}[] = [
	{ hours: 2, label: "2h", sub: "Quick poll", paidOnly: false },
	{ hours: 24, label: "24h", sub: "Fast track", paidOnly: true },
	{ hours: 72, label: "72h", sub: "Default", paidOnly: true },
	{ hours: 168, label: "168h", sub: "Slow burn", paidOnly: true },
];

const MAX_THESIS = 200;

// /fam/:slug/propose — proposal form. Renders symbol+direction+allocation+
// thesis+window inputs. On submit, posts to the Pages Function proxy which
// fans out to the vote service. Inline error states for the well-known wire
// codes (role_cannot_propose, ticker_below_floor, unknown_ticker, not_member).
export function ProposeIdeaRoute() {
	const { slug } = useParams<{ slug: string }>();
	const navigate = useNavigate();

	const famQuery = useQuery({
		queryKey: ["fam", slug],
		queryFn: () => {
			if (!slug) throw new Error("missing slug");
			return getFamBySlug(slug);
		},
		enabled: !!slug,
	});

	const create = useCreateIdea(slug ?? "");

	const [symbol, setSymbol] = useState("");
	const [direction, setDirection] = useState<IdeaDirection>("long");
	const [allocationPct, setAllocationPct] = useState<number>(5);
	const [thesis, setThesis] = useState("");
	const [voteWindowHours, setVoteWindowHours] = useState<VoteWindowHours>(2);
	const [voteWindowEdited, setVoteWindowEdited] = useState(false);
	const [currentUserId, setCurrentUserId] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		void getCurrentUser().then((u) => {
			if (!cancelled) setCurrentUserId(u?.id ?? null);
		});
		return () => {
			cancelled = true;
		};
	}, []);

	// Once the fam loads, bump the vote-window default to 72h for paid Fams.
	// Free Fams stay on the 2h floor (the only window the server accepts for
	// free tier). Bail if the user has already touched the selector so we
	// don't clobber their pick.
	const famTier = famQuery.data?.tier;
	useEffect(() => {
		if (famTier === "paid" && !voteWindowEdited) {
			setVoteWindowHours(72);
		}
	}, [famTier, voteWindowEdited]);

	if (!slug) {
		return (
			<div className="mx-auto max-w-2xl px-4 py-8 text-center text-sm text-[var(--color-danger)]">
				Missing fam slug.
			</div>
		);
	}

	if (famQuery.isLoading) {
		return (
			<div className="flex min-h-screen items-center justify-center text-[var(--color-muted)]">
				Loading Fam…
			</div>
		);
	}

	if (famQuery.isError || !famQuery.data) {
		return (
			<div className="mx-auto max-w-2xl px-4 py-8 text-center">
				<p className="text-sm text-[var(--color-danger)]">Could not load Fam.</p>
				<Link
					to="/"
					className="mt-4 inline-block rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)]"
				>
					Back to dashboard
				</Link>
			</div>
		);
	}

	const fam = famQuery.data;
	const myMember = currentUserId
		? fam.members.find((m) => m.userId === currentUserId)
		: undefined;
	const myRole = myMember?.role;
	const canPropose = myRole !== undefined && myRole !== "member";
	const isFreeTier = fam.tier === "free";

	const thresholdHint = (() => {
		switch (myRole) {
			case "pm":
				return "PM proposal — enters as a unilateral pick (0% vote threshold).";
			case "rm":
				return "RM proposal — needs 40% yes to pass.";
			case "trader":
				return "Trader proposal — needs 30% yes to pass.";
			case "analyst":
				return "Analyst proposal — needs 50% yes to pass.";
			default:
				return "Members cannot propose binding ideas.";
		}
	})();

	const onSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const cleaned = symbol.trim().toUpperCase();
		if (!cleaned || !thesis.trim() || allocationPct <= 0) return;
		const body: CreateIdeaRequest = {
			symbol: cleaned,
			direction,
			allocationPct,
			thesis: thesis.trim(),
			voteWindowHours,
		};
		create.mutate(body, {
			onSuccess: (idea) => {
				// React Router 6.4+ navigate() returns Promise<void> — void it
				// so eslint's no-floating-promises stays quiet; we don't need
				// to await the route transition here.
				void navigate(`/fam/${slug}/idea/${idea.id}`);
			},
		});
	};

	const errorMessage = (() => {
		if (!create.error) return null;
		if (create.error instanceof ApiClientError) {
			switch (create.error.code) {
				case "role_cannot_propose":
					return "Your role can't propose binding ideas. Ask the PM to upgrade you to Trader+.";
				case "ticker_below_floor":
					return "That ticker sits below the $500M market-cap floor. Hedge Fam restricts proposals to mid-/large-caps.";
				case "unknown_ticker":
					return "We couldn't resolve that ticker. Double-check the symbol.";
				case "not_member":
					return "You're not a member of this Fam.";
				case "bad_request":
				case "invalid_request":
					return create.error.message;
				default:
					return create.error.message;
			}
		}
		return "Could not submit proposal.";
	})();

	const formDisabled = !canPropose || create.isPending;

	return (
		<div className="mx-auto max-w-2xl space-y-6 px-4 py-8">
			<header>
				<Link
					to={`/fam/${slug}`}
					className="text-xs text-[var(--color-muted)] hover:text-[var(--color-fg)]"
				>
					← Back to {fam.name}
				</Link>
				<h1 className="mt-2 text-2xl font-bold">Propose an idea</h1>
				<p className="mt-1 text-sm text-[var(--color-muted)]">{thresholdHint}</p>
			</header>

			{!canPropose && currentUserId !== null && (
				<div className="rounded-lg border border-[var(--color-warning)]/40 bg-[var(--color-warning)]/10 p-4 text-sm text-[var(--color-warning)]">
					{myRole === "member"
						? "Members cannot propose binding ideas — ask the PM to upgrade your role."
						: "Only Fam members can propose ideas."}
				</div>
			)}

			<form
				onSubmit={onSubmit}
				className="space-y-5 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5"
			>
				<div className="space-y-1">
					<label
						htmlFor="symbol"
						className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted)]"
					>
						Symbol
					</label>
					<input
						id="symbol"
						type="text"
						value={symbol}
						onChange={(e) => setSymbol(e.target.value.toUpperCase())}
						placeholder="e.g. AAPL"
						maxLength={10}
						required
						disabled={formDisabled}
						className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 font-mono text-sm uppercase tracking-wider focus:border-[var(--color-accent)] focus:outline-none disabled:opacity-50"
					/>
					<p className="text-[11px] text-[var(--color-muted)]">
						US-listed tickers with $500M+ market cap. Sub-$500M proposals are
						rejected at the API.
					</p>
				</div>

				<div className="space-y-1">
					<span className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted)]">
						Direction
					</span>
					<div className="flex gap-2">
						<DirectionRadio
							value="long"
							active={direction === "long"}
							onClick={() => setDirection("long")}
							disabled={formDisabled}
						/>
						<DirectionRadio
							value="short"
							active={direction === "short"}
							onClick={() => setDirection("short")}
							disabled={formDisabled}
						/>
					</div>
				</div>

				<div className="space-y-2">
					<div className="flex items-center justify-between">
						<label
							htmlFor="allocation"
							className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted)]"
						>
							Allocation
						</label>
						<span className="text-sm font-semibold tabular-nums">
							{allocationPct.toFixed(2)}%
						</span>
					</div>
					<input
						id="allocation"
						type="range"
						min={0.25}
						max={100}
						step={0.25}
						value={allocationPct}
						onChange={(e) => setAllocationPct(Number(e.target.value))}
						disabled={formDisabled}
						className="w-full accent-[var(--color-accent)] disabled:opacity-50"
					/>
					<input
						type="number"
						min={0.25}
						max={100}
						step={0.25}
						value={allocationPct}
						onChange={(e) => {
							const n = Number(e.target.value);
							if (Number.isFinite(n)) setAllocationPct(n);
						}}
						disabled={formDisabled}
						className="w-28 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-sm tabular-nums focus:border-[var(--color-accent)] focus:outline-none disabled:opacity-50"
					/>
				</div>

				<div className="space-y-1">
					<div className="flex items-center justify-between">
						<label
							htmlFor="thesis"
							className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted)]"
						>
							Thesis
						</label>
						<span
							className={`text-xs tabular-nums ${
								thesis.length > MAX_THESIS
									? "text-[var(--color-danger)]"
									: "text-[var(--color-muted)]"
							}`}
						>
							{thesis.length}/{MAX_THESIS}
						</span>
					</div>
					<textarea
						id="thesis"
						value={thesis}
						onChange={(e) => setThesis(e.target.value.slice(0, MAX_THESIS))}
						rows={4}
						maxLength={MAX_THESIS}
						placeholder="Why this trade? One short paragraph the Fam can read in a glance."
						required
						disabled={formDisabled}
						className="w-full resize-none rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm focus:border-[var(--color-accent)] focus:outline-none disabled:opacity-50"
					/>
				</div>

				<div className="space-y-2">
					<span className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted)]">
						Vote window
					</span>
					<div className="grid grid-cols-4 gap-2">
						{VOTE_WINDOWS.map((w) => {
							const locked = isFreeTier && w.paidOnly;
							return (
								<button
									key={w.hours}
									type="button"
									disabled={formDisabled || locked}
									onClick={() => {
										setVoteWindowHours(w.hours);
										setVoteWindowEdited(true);
									}}
									className={`rounded-md border px-3 py-2 text-left text-sm transition disabled:cursor-not-allowed disabled:opacity-50 ${
										voteWindowHours === w.hours
											? "border-[var(--color-accent)] bg-[var(--color-accent-soft)] text-[var(--color-fg)]"
											: "border-[var(--color-border)] hover:border-[var(--color-accent)]"
									}`}
								>
									<div className="font-semibold">{w.label}</div>
									<div className="text-[11px] text-[var(--color-muted)]">
										{locked ? "Upgrade" : w.sub}
									</div>
								</button>
							);
						})}
					</div>
					{isFreeTier && (
						<p className="text-[11px] text-[var(--color-muted)]">
							Free Fams use the 2h window. Upgrade to unlock 24h / 72h /
							168h.
						</p>
					)}
				</div>

				{errorMessage && (
					<p className="rounded-md border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/10 px-3 py-2 text-xs text-[var(--color-danger)]">
						{errorMessage}
					</p>
				)}

				<div className="flex items-center justify-end gap-2">
					<Link
						to={`/fam/${slug}`}
						className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)]"
					>
						Cancel
					</Link>
					<button
						type="submit"
						disabled={
							formDisabled || thesis.trim() === "" || symbol.trim() === ""
						}
						className="rounded-md bg-[var(--color-accent)] px-4 py-1.5 text-sm font-semibold text-black transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
					>
						{create.isPending ? "Submitting…" : "Propose idea"}
					</button>
				</div>
			</form>

			{/* Live backtest preview. Debounced inside BacktestCard; updates as
			    the user iterates on symbol / direction / allocation. Paid users
			    see the full risk-adjusted metrics; free users see only Total
			    Return with the rest blurred + an Upgrade CTA overlay. */}
			<BacktestCard
				symbol={symbol}
				direction={direction}
				allocationPct={allocationPct}
			/>
		</div>
	);
}

interface DirectionRadioProps {
	value: IdeaDirection;
	active: boolean;
	onClick: () => void;
	disabled: boolean;
}

function DirectionRadio({ value, active, onClick, disabled }: DirectionRadioProps) {
	const palette =
		value === "long"
			? {
					active: "border-emerald-500 bg-emerald-500/15 text-emerald-300",
					idle: "border-[var(--color-border)] hover:border-emerald-500",
				}
			: {
					active: "border-[var(--color-danger)] bg-[var(--color-danger)]/15 text-[var(--color-danger)]",
					idle: "border-[var(--color-border)] hover:border-[var(--color-danger)]",
				};
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold uppercase tracking-wider transition disabled:cursor-not-allowed disabled:opacity-50 ${
				active ? palette.active : palette.idle
			}`}
		>
			{value}
		</button>
	);
}
